import React, { useCallback, useEffect, useRef, useState } from "react";
import { animated, useSpring } from "react-spring";
import { useDrag } from "react-use-gesture";

import translateIcon from "../../assets/icons/move.svg";
import ProgressCircle from "./ProgressCircle";
import ImageLoader from "../images/ImageLoader";
import useMounted from "../../hooks/useMounted";

const getScaleAmount = (dx, dy) => {
  return dx - dy;
};

function ScaleTransform({
  uploading,
  x,
  y,
  active,
  height,
  width,
  setTempScale,
  handleScale,
  aspectRatio,
}) {
  const containerRef = useRef();

  const bindTopLeft = useDrag(
    ({ movement: [dx, dy], down, event }) => {
      event && event.stopPropagation();
      setTempScale(getScaleAmount(dx * -1, dy));
      if (!down) {
        handleScale(getScaleAmount(dx * -1, dy));
      }
    },
    { eventOptions: { passive: false, capture: active } }
  );
  const bindTopRight = useDrag(
    ({ movement: [dx, dy], down, event }) => {
      event && event.stopPropagation();
      setTempScale(getScaleAmount(dx, dy));
      if (!down) {
        handleScale(getScaleAmount(dx, dy));
      }
    },
    { eventOptions: { passive: false, capture: active } }
  );
  const bindBottomLeft = useDrag(
    ({ movement: [dx, dy], down, event }) => {
      event && event.stopPropagation();
      setTempScale(getScaleAmount(dx * -1, dy * -1));
      if (!down) {
        handleScale(getScaleAmount(dx * -1, dy * -1));
      }
    },
    { eventOptions: { passive: false, capture: active } }
  );

  const bindBottomRight = useDrag(
    ({ movement: [dx, dy], down, event }) => {
      event && event.stopPropagation();
      setTempScale(getScaleAmount(dx, dy * -1));
      if (!down) {
        handleScale(getScaleAmount(dx, dy * -1));
      }
    },
    { eventOptions: { passive: false, capture: active } }
  );

  return (
    <animated.div
      ref={containerRef}
      style={{
        height,
        width,
        pointerEvents: "none",
        zIndex: 3,
        opacity: uploading ? 0 : 1,
        transition: "opacity 500ms",
      }}
    >
      <svg
        {...bindTopLeft()}
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        style={{
          position: "absolute",
          pointerEvents: active ? "auto" : "none",
          top: -10,
          left: -10,
          height: 25,
          width: 25,
        }}
      >
        <path
          d="M 0 100 L 0 0 L 100 0"
          stroke="white"
          strokeWidth="45"
          fill="none"
        />
      </svg>
      <svg
        {...bindBottomLeft()}
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        style={{
          position: "absolute",
          pointerEvents: active ? "auto" : "none",
          bottom: -10,
          left: -10,
          height: 25,
          width: 25,
        }}
      >
        <path
          d="M 0 0 L 0 100 L 100 100"
          stroke="white"
          strokeWidth="45"
          fill="none"
        />
      </svg>
      <svg
        {...bindTopRight()}
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        style={{
          position: "absolute",
          pointerEvents: active ? "auto" : "none",
          top: -10,
          right: -10,
          height: 25,
          width: 25,
        }}
      >
        <path
          d="M 0 0 L 100 0 L 100 100"
          stroke="white"
          strokeWidth="45"
          fill="none"
        />
      </svg>
      <svg
        {...bindBottomRight()}
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        style={{
          position: "absolute",
          pointerEvents: active ? "auto" : "none",
          bottom: -10,
          right: -10,
          height: 25,
          width: 25,
        }}
      >
        <path
          d="M 100 0 L 100 100 L 0 100"
          stroke="white"
          strokeWidth="45"
          fill="none"
        />
      </svg>
    </animated.div>
  );
}

function TranslateTransform({
  setTempMovement,
  handleMove,
  width,
  height,
  selected,
  uploading,
  active,
}) {
  const bind = useDrag(
    ({ down, movement: [mx, my], event }) => {
      event && event.stopPropagation();
      if (selected && !uploading) {
        setTempMovement({ mx, my });
        if (!down) handleMove({ mx, my });
      }
    },
    { eventOptions: { passive: false, capture: active } }
  );

  return (
    <animated.div
      style={{
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        width,
        height,
        alignItems: "center",
        filter: "drop-shadow(0px 0px 3px #777)",
        pointerEvents: "none",
        opacity: uploading ? 0 : 1,
        transition: "opacity 500ms",
      }}
    >
      <animated.img
        {...bind()}
        draggable={false}
        src={translateIcon}
        style={{
          height: "33%",
          pointerEvents: uploading ? "none" : active ? "auto" : "none",
        }}
        alt="move"
      />
    </animated.div>
  );
}

function ItemOverlay({
  uploading,
  error,
  active,
  progress,
  x,
  y,
  aspectRatio,
  height,
  width,
  itemId,
  selected,
  setTempMovement,
  setTempScale,
  tempScale,
  handleMove,
  handleScale,
}) {
  const { opacity } = useSpring({
    opacity: selected ? 1 : 0,
  });

  return (
    <animated.div
      style={{
        position: "absolute",
        height,
        width,
        left: 0,
        top: 0,
        background: "rgba(255,255,255,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        pointerEvents: uploading && active ? "auto" : "none",
        opacity,
      }}
    >
      <ProgressCircle progress={progress} color="white" />
      <ScaleTransform
        aspectRatio={aspectRatio}
        active={active}
        handleScale={handleScale}
        height={height + tempScale}
        x={x}
        y={y}
        setTempScale={setTempScale}
        uploading={uploading}
      />
      <TranslateTransform
        active={active}
        width={width}
        height={height}
        selected={selected}
        uploading={uploading}
        handleMove={handleMove}
        setTempMovement={setTempMovement}
      />
      <span
        style={{
          position: "absolute",
          color: "#444",
          width: "100%",
          padding: "0px 16px",
          boxSizing: "border-box",
          textAlign: "center",
        }}
      >
        {error}
      </span>
    </animated.div>
  );
}

export default function MoodboardItem({
  x,
  y,
  height: propHeight,
  editActive,
  aspectRatio,
  selected,
  dataUrl,
  deleted,
  itemId,
  url,
  handleMove,
  handleScale,
  uploading,
  progress,
  error,
  index,
  select,
  zIndex = 0,
}) {
  const [hovered, setHovered] = useState(false);

  // const [{ tempMovement }, setTempMovement] = useSpring(() => ({
  //     tempMovement: [0, 0],
  // }));

  const [{ mx, my }, setTempMovement] = useState({ mx: 0, my: 0 });
  const [tempScale, setTempScale] = useState(0);

  const mounted = useMounted();

  const { xy, height, width, filter, opacity } = useSpring({
    opacity: mounted && !deleted ? 1 : 0,
    xy: [(x || 0) + mx, (y || 0) + my],
    height: propHeight + tempScale,
    width: (propHeight + tempScale) * aspectRatio,
    shadow: hovered ? 4 : 3,
    filter: `drop-shadow(0px 0px ${selected ? 5 : 3}px #666)`,
  });

  useEffect(() => {
    setTempMovement({ mx: 0, my: 0 });
  }, [x, y]);

  useEffect(() => {
    setTempScale(0);
  }, [propHeight]);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      select();
    },
    [select]
  );

  const movementCallback = () => setTempMovement({ mx: 0, my: 0 });

  const scaleCallback = () => setTempScale(0);

  return (
    <animated.div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height,
        width,
        pointerEvents: deleted ? "none" : "auto",
        cursor: "pointer",
        overflow: "visible",
        opacity,
        zIndex: selected ? 9999 : zIndex,
        transform: xy.interpolate((x, y) => `translate3d(${x}px, ${y}px, 0)`),
        filter,
      }}
    >
      <ImageLoader
        src={dataUrl || url}
        alt={`moodboard_item_${itemId}`}
        height={height}
        width={width}
      />
      {/* <animated.img
                draggable={false}
                src={dataUrl || url}
                alt={`moodboard_item_${itemId}`}
                style={{
                    height,
                }}
                scale={scale}
            /> */}
      <ItemOverlay
        x={x}
        y={y}
        active={editActive && selected}
        height={height}
        width={width}
        aspectRatio={aspectRatio}
        setTempMovement={setTempMovement}
        tempScale={tempScale}
        setTempScale={setTempScale}
        handleMove={(movement) => handleMove(movement, movementCallback)}
        handleScale={(scaleAmount) => handleScale(scaleAmount, scaleCallback)}
        selected={selected}
        error={error}
        uploading={uploading}
        progress={progress}
        itemId={itemId}
      />
    </animated.div>
  );
}
